import React, { useState, useEffect, useMemo } from 'react';
import { flushSync, createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, getDoc, setDoc, onSnapshot, query, collection, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useGamerAuth } from '../GamerAuthContext';
import BgmiFooter from './BgmiFooter';

// Icons
const IconUsers = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>;
const IconTrophy = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>;
const IconGamepad = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const IconShield = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>;
const IconWallet = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>;
const IconLightning = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>;
const IconTarget = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>;
const IconMobile = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>;
const IconStar = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>;

const IconTicket = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>;
const IconTrophySmall = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>;

const colorsByDate = {
    0: ['#ec4899', '#a855f7'], // Pink & Purple
    '-1': ['#3b82f6', '#8b5cf6'], // Blue & Purple
    '-2': ['#10b981', '#3b82f6'], // Emerald & Blue
    '-3': ['#f59e0b', '#ef4444'], // Amber & Red
    '-4': ['#14b8a6', '#0ea5e9'], // Teal & Sky
    '-5': ['#f97316', '#eab308'], // Orange & Yellow
    '-6': ['#8b5cf6', '#d946ef'], // Violet & Fuchsia
};

const TournamentCard = ({ id, docId, dateStr, bookedSlots, title, map, mode, entry, prize, killReward, slots, start, time, lobbyType = 'CLEAN LOBBY', isFeatured = false, isCompleted = false, isOngoing = false, livestreamLink = 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', borderColor1 = '#a855f7', borderColor2 = '#ec4899', onJoinClick, initialHasJoined = false, isOnboardingPaused = false, resultPublished = false, resultsData, roomId, roomPassword, onIdPassClick }) => {
    const [isJoining, setIsJoining] = useState(false);
    const [hasJoined, setHasJoined] = useState(initialHasJoined);
    const { gamerUser } = useGamerAuth();
    const isBanned = gamerUser?.isBanned || gamerUser?.calculatedTrustScore <= 0;

    useEffect(() => {
        setHasJoined(initialHasJoined);
    }, [initialHasJoined]);

    const handleJoin = () => {
        if (isJoining || isCompleted || isOnboardingPaused) return;
        if (hasJoined && roomId && onIdPassClick) {
            onIdPassClick({ roomId, roomPassword, title });
            return;
        }
        if (onJoinClick) {
            onJoinClick({ id, docId, dateStr, bookedSlots, title, map, mode, entry, prize, killReward, slots, start, time, lobbyType, isOnboardingPaused });
            return;
        }
        setIsJoining(true);
        setTimeout(() => {
            setIsJoining(false);
            setHasJoined(true);
        }, 2000);
    };

    const [filledStr, totalStr] = slots.split(' / ');
    const filled = parseInt(filledStr) || 0;
    const total = parseInt(totalStr) || 100;
    const fillPercentage = Math.min(100, Math.max(0, (filled / total) * 100));

    const timeTitle = (isCompleted || isOngoing) ? 'STARTED AT' : 'STARTS AT';

    const entryValue = parseInt((entry || "0").replace(/\D/g, '')) || 0;
    const rsValue = entryValue * 1.25;
    const entryDisplay = entry?.includes('GLPC') ? `${entry} = ₹ ${rsValue}` : entry;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className={`uppercase font-['Monda'] relative overflow-hidden ${isFeatured ? 'rounded-2xl' : 'rounded-none'} p-[2px] group h-full ${isFeatured ? 'shadow-[0_0_30px_rgba(168,85,247,0.1)] dark:shadow-[0_0_30px_rgba(168,85,247,0.2)]' : ''}`}
        >
            <div className="absolute inset-[-150%] transform-gpu will-change-transform opacity-0 group-hover:opacity-100 group-hover:animate-[spin_4s_linear_infinite] transition-opacity duration-500" style={{ backgroundImage: `conic-gradient(from 90deg at 50% 50%, transparent 0%, ${borderColor1} 25%, ${borderColor2} 50%, ${borderColor1} 75%, transparent 100%)` }} />

            <div className={`relative z-10 w-full h-full bg-white dark:bg-[#1a1a24] ${isFeatured ? 'rounded-xl' : 'rounded-none'}`}>
                {isFeatured && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 z-20" />
                )}
                <div className={`p-[15px] h-full ${isFeatured ? 'grid md:grid-cols-2 gap-8 items-center' : ''}`}>
                    {isFeatured && (
                        <div className="relative rounded-2xl overflow-hidden aspect-video">
                            <img src="https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=800&q=80" alt="Tournament" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            <div className="absolute top-4 left-4 bg-pink-500 text-white text-xs font-bold px-3 py-1 rounded-sm uppercase tracking-wider">
                                Tournament of the Day
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col h-full">
                        <div className="flex justify-between items-start mb-6">
                            <div className="flex flex-col gap-2">
                                <h3 className={`${isFeatured ? 'text-3xl md:text-4xl' : 'text-2xl'} font-black text-gray-900 dark:text-white uppercase tracking-wide mt-[-2px]`}>{title}</h3>
                                <span className={`text-[10px] font-black px-2 py-0.5 rounded w-fit uppercase tracking-widest ${lobbyType === 'HACKER vs HACKER' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-green-500/10 text-green-500 border border-green-500/20'}`}>{lobbyType}</span>
                            </div>
                            <div className="flex flex-wrap gap-2 justify-end">
                                <span className="text-xs font-bold px-3 py-1 rounded-none" style={{ backgroundColor: `${borderColor1}33`, color: borderColor1 }}>{mode}</span>
                                <span className="text-xs font-bold px-3 py-1 rounded-none" style={{ backgroundColor: `${borderColor2}33`, color: borderColor2 }}>{map}</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-gray-50 dark:bg-white/5 p-3 rounded-none border border-gray-100 dark:border-white/5">
                                <p className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-[10px] sm:text-xs uppercase mb-1">
                                    <IconTicket /> PARTICIPATION FEE
                                </p>
                                <p className="text-gray-900 dark:text-white font-bold text-[13px]">{entryDisplay}</p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-500/20 dark:to-pink-500/20 p-3 rounded-none border border-pink-100 dark:border-pink-500/20" style={{ borderColor: `${borderColor2}33`, background: `linear-gradient(to bottom right, ${borderColor1}11, ${borderColor2}11)` }}>
                                <p className="flex items-center gap-1.5 text-[10px] sm:text-xs uppercase mb-1" style={{ color: borderColor2 }}>
                                    <IconTrophySmall /> DYNAMIC PRIZE POOL
                                </p>
                                <p className="text-gray-900 dark:text-white font-bold text-[13px]">{prize}</p>
                            </div>
                        </div>

                        <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-300 mb-2">
                            <div className="flex items-center gap-1.5">{killReward} / KILL BONUS</div>
                            <div className="flex items-center gap-1.5"><span className="text-gray-900 dark:text-white font-bold">{slots}</span> FILLED</div>
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 dark:bg-white/10 rounded-none mb-6 overflow-hidden">
                            <div className="h-full rounded-none transition-all duration-1000 ease-out" style={{ width: `${fillPercentage}%`, backgroundImage: `linear-gradient(to right, ${borderColor1}, ${borderColor2})` }} />
                        </div>

                        <div className="flex items-stretch justify-between gap-4 mt-auto">
                            <div className="flex flex-col justify-between py-1 text-left">
                                <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 uppercase leading-none mb-2">{timeTitle}</p>
                                <p className="text-lg font-bold text-pink-600 dark:text-pink-400 leading-none">{time}</p>
                            </div>
                            <button
                                onClick={isCompleted ? () => onJoinClick && onJoinClick({ id, docId, dateStr, bookedSlots, title, map, mode, entry, prize, killReward, slots, start, time, isCompleted, lobbyType, isOnboardingPaused, resultPublished, resultsData }) : isOngoing ? () => window.open(livestreamLink, '_blank') : handleJoin}
                                disabled={(!isOngoing && isBanned) || isJoining || (!isCompleted && !isOngoing && isOnboardingPaused) || (isCompleted && !resultPublished)}
                                className={`px-6 py-3 rounded-[50px] font-bold uppercase tracking-wider transition-all duration-300 font-['Iceland'] text-xl flex items-center justify-center gap-2 ${(!isOngoing && isBanned)
                                    ? 'bg-red-900/50 text-red-500 cursor-not-allowed border-2 border-red-500/50'
                                    : (isCompleted && !resultPublished)
                                        ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                    : isCompleted
                                        ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer shadow-[0_0_15px_rgba(37,99,235,0.4)]'
                                        : isOngoing
                                            ? 'bg-red-600 text-white hover:bg-red-700 cursor-pointer shadow-[0_0_15px_rgba(220,38,38,0.4)]'
                                            : isOnboardingPaused
                                                ? 'bg-gray-600 text-gray-300 cursor-not-allowed opacity-80'
                                                : hasJoined
                                                    ? 'bg-green-600 text-white hover:bg-green-700 shadow-[0_0_15px_rgba(22,163,74,0.4)] cursor-pointer'
                                                    : isJoining
                                                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white opacity-80 cursor-wait'
                                                        : isFeatured
                                                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-[0_0_20px_rgba(236,72,153,0.4)] hover:scale-105'
                                                            : 'bg-green-500 text-white hover:bg-green-600 shadow-[0_0_15px_rgba(34,197,94,0.3)] cursor-pointer'
                                    }`}
                            >
                                {(!isOngoing && isBanned) ? (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                        BANNED
                                    </>
                                ) : (isCompleted && !resultPublished) ? 'UNAVAILABLE' : isCompleted ? 'RESULT' : isOngoing ? (
                                    <>
                                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                                        WATCH LIVE
                                    </>
                                ) : isOnboardingPaused ? (
                                    'PAUSED'
                                ) : isJoining ? (
                                    <>
                                        <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        JOINING
                                    </>
                                ) : hasJoined ? (
                                    roomId ? (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
                                            ID / PASS
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                            JOINED
                                        </>
                                    )
                                ) : 'JOIN NOW'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const FeatureCard = ({ title, icon }) => (
    <div className="flex flex-col items-center p-6 bg-gradient-to-br from-purple-500/5 to-pink-500/5 dark:from-purple-900/20 dark:to-pink-900/10 rounded-2xl border border-purple-200 dark:border-purple-500/20 hover:border-pink-300 dark:hover:border-pink-500/40 transition-colors group shadow-sm dark:shadow-none transform-gpu">
        <div className="w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-500/10 flex items-center justify-center text-purple-600 dark:text-purple-400 mb-4 group-hover:scale-110 transition-transform">
            {icon}
        </div>
        <h4 className="text-sm font-['Monda'] font-bold text-gray-900 dark:text-white text-center uppercase tracking-wider">{title}</h4>
    </div>
);

const StepCard = ({ num, title }) => (
    <div className="flex flex-col items-center relative z-10">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xl mb-3 shadow-[0_0_15px_rgba(168,85,247,0.3)] dark:shadow-[0_0_15px_rgba(168,85,247,0.5)]">
            {num}
        </div>
        <p className="text-gray-900 dark:text-white font-bold text-center font-['Monda'] text-sm uppercase tracking-wider whitespace-nowrap">{title}</p>
    </div>
);


const generateDates = () => {
    const dates = [];
    const today = new Date();
    // Generate exactly 7 dates, starting from 3 days ago up to 3 days from now
    for (let i = -3; i <= 3; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        dates.push({
            dateObj: d,
            offset: i,
            dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
            dayNum: d.getDate(),
            month: d.toLocaleDateString('en-US', { month: 'short' })
        });
    }
    return dates;
};

const ScreenResolution = () => {
    const [res, setRes] = useState({ w: typeof window !== 'undefined' ? window.innerWidth : 0, h: typeof window !== 'undefined' ? window.innerHeight : 0 });
    useEffect(() => {
        const handleResize = () => setRes({ w: window.innerWidth, h: window.innerHeight });
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return (
        <div className="fixed bottom-4 right-4 z-50 bg-black/80 text-white px-3 py-1.5 rounded-lg text-xs font-mono font-bold backdrop-blur-sm border border-white/20 pointer-events-none shadow-lg hidden md:block">
            {res.w} x {res.h}
        </div>
    );
};

const SkeletonCard = ({ borderColor1 = '#a855f7', borderColor2 = '#ec4899' }) => (
    <div className="uppercase relative overflow-hidden rounded-none p-[2px] h-full transition-all duration-300 group">
        <div className="absolute inset-[-150%] transform-gpu will-change-transform opacity-0 group-hover:opacity-100 group-hover:animate-[spin_4s_linear_infinite] transition-opacity duration-500" style={{ backgroundImage: `conic-gradient(from 90deg at 50% 50%, transparent 0%, ${borderColor1} 25%, ${borderColor2} 50%, ${borderColor1} 75%, transparent 100%)` }} />
        <div className="relative z-10 w-full h-full bg-white dark:bg-[#1a1a24] p-[15px] flex flex-col h-full">
            <div className="flex flex-col h-full animate-pulse">
                <div className="flex justify-between items-start mb-6">
                    <div className="flex flex-col gap-2 w-1/2">
                        <div className="h-8 bg-gray-200 dark:bg-white/10 rounded-none w-2/3 mt-[-2px]" />
                        <div className="h-[18px] bg-gray-200 dark:bg-white/10 rounded w-1/3" />
                    </div>
                    <div className="flex gap-2">
                        <div className="h-6 bg-gray-200 dark:bg-white/10 rounded-none w-14" />
                        <div className="h-6 bg-gray-200 dark:bg-white/10 rounded-none w-14" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-100 dark:bg-white/5 p-3 rounded-none border border-gray-200 dark:border-white/5">
                        <p className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-[10px] sm:text-xs uppercase mb-1"><IconTicket /> PARTICIPATION FEE</p>
                        <div className="h-6 bg-gray-200 dark:bg-white/10 rounded-none w-3/4" />
                    </div>
                    <div className="bg-gray-100 dark:bg-white/5 p-3 rounded-none border border-gray-200 dark:border-white/5">
                        <p className="flex items-center gap-1.5 text-pink-600 dark:text-pink-300 text-[10px] sm:text-xs uppercase mb-1"><IconTrophySmall /> DYNAMIC PRIZE POOL</p>
                        <div className="h-6 bg-gray-200 dark:bg-white/10 rounded-none w-3/4" />
                    </div>
                </div>
                <div className="flex justify-between items-center mb-2">
                    <div className="h-5 bg-gray-200 dark:bg-white/10 rounded-none w-1/3" />
                    <div className="h-5 bg-gray-200 dark:bg-white/10 rounded-none w-1/4" />
                </div>
                <div className="w-full h-1.5 bg-gray-200 dark:bg-white/10 rounded-none mb-6" />
                <div className="flex items-stretch justify-between gap-4 mt-auto">
                    <div className="flex flex-col justify-between py-1 text-left w-1/3">
                        <p className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 uppercase leading-none mb-2">MATCH TIME</p>
                        <div className="h-5 bg-gray-200 dark:bg-white/10 rounded-none w-3/4" />
                    </div>
                    <div className="h-12 bg-gray-200 dark:bg-white/10 rounded-[50px] w-32" />
                </div>
            </div>
        </div>
    </div>
);


export default function BgmiEsportsPlatform() {
    const { gamerUser } = useGamerAuth();
    const [activeLobby, setActiveLobby] = useState('MOBILE');
    const [activeHall, setActiveHall] = useState('FAME');
    const [selectedDateOffset, setSelectedDateOffset] = useState(0);
    const [showStaffModal, setShowStaffModal] = useState(false);
    const [isStaffRoleDropdownOpen, setIsStaffRoleDropdownOpen] = useState(false);
    const [staffRole, setStaffRole] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [idPassModalData, setIdPassModalData] = useState(null);
    const [loadedDates, setLoadedDates] = useState(new Set([0])); // Today is pre-loaded
    const datesList = useMemo(() => generateDates(), []);
    const [matches, setMatches] = useState([]);
    const [hallOfFamePlayers, setHallOfFamePlayers] = useState([]);
    const [isLoadingHallOfFame, setIsLoadingHallOfFame] = useState(true);
    const [hallOfShamePlayers, setHallOfShamePlayers] = useState([]);
    const [isLoadingHallOfShame, setIsLoadingHallOfShame] = useState(true);
    const [toastMessage, setToastMessage] = useState(null);

    useEffect(() => {
        const leaderboardQuery = query(collection(db, 'leaderboard'), orderBy('earnings', 'desc'), limit(30));

        const unsubscribe = onSnapshot(leaderboardQuery, (snapshot) => {
            const players = [];
            let rank = 1;
            snapshot.forEach((doc) => {
                players.push({
                    rank: rank++,
                    ...doc.data()
                });
            });
            setHallOfFamePlayers(players);
            setIsLoadingHallOfFame(false);
        }, (error) => {
            console.error("Error fetching leaderboard in real-time:", error);
            setIsLoadingHallOfFame(false);
        });

        const shameboardQuery = query(collection(db, 'shameboard'), orderBy('earnings', 'desc'), limit(30));
        const unsubscribeShame = onSnapshot(shameboardQuery, (snapshot) => {
            const players = [];
            let rank = 1;
            snapshot.forEach((doc) => {
                players.push({
                    rank: rank++,
                    ...doc.data()
                });
            });
            setHallOfShamePlayers(players);
            setIsLoadingHallOfShame(false);
        }, (error) => {
            console.error("Error fetching shameboard in real-time:", error);
            setIsLoadingHallOfShame(false);
        });

        return () => {
            unsubscribe();
            unsubscribeShame();
        };
    }, []);

    useEffect(() => {
        if (toastMessage) {
            const timer = setTimeout(() => setToastMessage(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toastMessage]);

    // Removed uploadAllDummyData background task as per user request

    useEffect(() => {
        const selectedDateObj = datesList.find(d => d.offset === selectedDateOffset)?.dateObj;
        if (!selectedDateObj) return;

        // Generate YYYY-MM-DD
        const year = selectedDateObj.getFullYear();
        const month = String(selectedDateObj.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDateObj.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        const docId = activeLobby === 'MOBILE' ? 'mobileMatches' : 'emulatorMatches';
        const docRef = doc(db, 'dailyMatches', docId, 'dates', dateStr);

        // Store these for use in the card
        window.currentDateStr = dateStr;
        window.currentDocId = docId;

        setIsLoading(true);
        const unsubscribe = onSnapshot(docRef, async (docSnap) => {
            try {
                if (docSnap.exists()) {
                    const latestMatches = docSnap.data().data || [];
                    setMatches(latestMatches);
                    window.dispatchEvent(new CustomEvent('matchesUpdated', { detail: latestMatches }));
                } else {
                    setMatches([]);
                }
            } catch (error) {
                console.error("Error fetching daily matches:", error);
                setMatches([]);
            }
        }, (error) => {
            console.error("Snapshot error:", error);
        });

        return () => unsubscribe();
    }, [activeLobby, selectedDateOffset, datesList]);

    useEffect(() => {
        if (isLoading) {
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    const handleTournamentJoinClick = async (tournament) => {
        if (!gamerUser) {
            setToastMessage("PLEASE LOGIN TO YOUR ACCOUNT");
            return;
        }

        if (!tournament.isCompleted) {
            let computedTrustScore = gamerUser?.trustScore ?? 100;
            if (computedTrustScore < 0) computedTrustScore = 0;

            if (computedTrustScore <= 40) {
                setToastMessage("ACCOUNT INACTIVE: YOUR TRUST SCORE IS TOO LOW");
                return;
            }
        }

        if (tournament.isCompleted) {
            const uid1 = gamerUser?.profileData?.gameUid1;
            const uid2 = gamerUser?.profileData?.gameUid2;
            const hasParticipatedServer = tournament.bookedSlots && tournament.bookedSlots.some(slot => slot.uid === uid1 || slot.uid === uid2);

            if (!hasParticipatedServer) {
                setToastMessage("SORRY! YOU DIDN'T PARTICIPATE IN THIS MATCH");
                return;
            }
            window.dispatchEvent(new CustomEvent('openTournamentResult', { detail: tournament }));
        } else {
            const entryValue = parseInt((tournament.entry || "0").replace(/\D/g, '')) || 0;
            if (entryValue > 0) {
                const userId = gamerUser.uid || gamerUser.gamerCode;
                if (userId) {
                    try {
                        const vaultRef = doc(db, 'users', userId, 'transactions', 'vaultData');
                        const vaultSnap = await getDoc(vaultRef);
                        const currentBalance = vaultSnap.exists() ? (vaultSnap.data().eCoinsBalance || 0) : 0;
                        if (currentBalance < entryValue) {
                            setToastMessage(`INSUFFICIENT BALANCE! YOU NEED ${entryValue} GLPC`);
                            return;
                        }
                    } catch (e) {
                        console.error("Error fetching balance:", e);
                    }
                }
            }
            window.dispatchEvent(new CustomEvent('openTournamentSlots', { detail: tournament }));
        }
    };

    return (
        <div className="bg-transparent dark:bg-[#0a0a0f] min-h-screen text-gray-900 dark:text-white selection:bg-pink-500/30 relative">
            
            {/* ID/PASS Modal */}
            <AnimatePresence>
                {idPassModalData && (
                    <motion.div 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                    >
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="bg-white dark:bg-[#1a1a24] rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl border border-gray-200 dark:border-white/10"
                        >
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6 border-b border-gray-200 dark:border-white/10 pb-4">
                                    <h3 className="text-xl font-black uppercase text-gray-900 dark:text-white">MATCH ID / PASS</h3>
                                    <button onClick={() => setIdPassModalData(null)} className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                                
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 uppercase text-center font-bold">
                                    {idPassModalData.title}
                                </p>

                                <div className="space-y-4">
                                    <div>
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1 block">ROOM ID</label>
                                        <div className="flex bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
                                            <input 
                                                type="text" 
                                                readOnly 
                                                value={idPassModalData.roomId} 
                                                className="w-full bg-transparent px-4 py-3 font-black text-lg text-center outline-none text-gray-900 dark:text-white"
                                            />
                                            <button 
                                                onClick={() => {
                                                    navigator.clipboard.writeText(idPassModalData.roomId);
                                                    setToastMessage("ROOM ID COPIED!");
                                                }}
                                                className="bg-purple-500 hover:bg-purple-600 px-4 text-white font-bold text-xs transition-colors flex items-center justify-center gap-2"
                                            >
                                                COPY
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1 block">PASSWORD</label>
                                        <div className="flex bg-gray-50 dark:bg-black/50 border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden">
                                            <input 
                                                type="text" 
                                                readOnly 
                                                value={idPassModalData.roomPassword} 
                                                className="w-full bg-transparent px-4 py-3 font-black text-lg text-center outline-none text-gray-900 dark:text-white"
                                            />
                                            <button 
                                                onClick={() => {
                                                    navigator.clipboard.writeText(idPassModalData.roomPassword);
                                                    setToastMessage("PASSWORD COPIED!");
                                                }}
                                                className="bg-pink-500 hover:bg-pink-600 px-4 text-white font-bold text-xs transition-colors flex items-center justify-center gap-2"
                                            >
                                                COPY
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <button 
                                        onClick={() => setIdPassModalData(null)}
                                        className="w-full py-3 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 rounded-xl text-gray-900 dark:text-white font-black uppercase text-sm transition-colors"
                                    >
                                        CLOSE
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {toastMessage && (
                    <div className="fixed inset-x-0 top-20 md:top-6 z-[999999] flex justify-center pointer-events-none px-4">
                        <motion.div
                            initial={{ opacity: 0, y: -20, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -20, scale: 0.9 }}
                            className="bg-red-500 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-black tracking-widest text-[10px] sm:text-sm uppercase shadow-[0_0_20px_rgba(239,68,68,0.5)] border border-red-400 max-w-full text-center pointer-events-auto"
                        >
                            {toastMessage}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
            <div className="absolute top-[10%] left-[-10%] w-[40vw] h-[40vw] bg-purple-500/10 dark:bg-purple-600/10 rounded-full blur-[120px] pointer-events-none -z-10 transform translate-z-0"></div>
            <div className="absolute bottom-[10%] right-[-10%] w-[40vw] h-[40vw] bg-pink-500/10 dark:bg-pink-600/10 rounded-full blur-[120px] pointer-events-none -z-10 transform translate-z-0"></div>
            <ScreenResolution />
            <div className="max-w-[120rem] mx-auto px-[5px] lg:px-[15px] space-y-12">

                {/* Section 2: Tabbed Lobby */}
                <section className="relative z-20 -mt-[31px]">
                    <div className="flex flex-col items-center justify-center gap-8 mb-12">
                        {/* Tab Switcher */}
                        <div className="flex items-center justify-center w-full px-4">
                            {/* Left Separator Line */}
                            <div className="flex-1 h-[2px] bg-gradient-to-l from-gray-300 dark:from-white/20 to-transparent"></div>
                            
                            <div className="flex w-full max-w-lg shrink-0 bg-white dark:bg-[#1a1a24] p-1.5 rounded-[50px] shadow-2xl border border-gray-100 dark:border-white/10 relative z-10 -mx-1">
                                {/* Sliding Pill Background */}
                                <div
                                    className="absolute top-1.5 bottom-1.5 left-1.5 w-[calc(50%-6px)] bg-gradient-to-r from-purple-600 to-pink-600 rounded-[50px] shadow-[0_0_15px_rgba(219,39,119,0.5)] transition-transform duration-300 ease-out z-0"
                                    style={{ transform: activeLobby === 'MOBILE' ? 'translateX(0)' : 'translateX(100%)' }}
                                />
                                
                                <button
                                    onClick={() => setActiveLobby('MOBILE')}
                                    className={`flex-1 py-3 px-4 sm:px-6 rounded-[50px] font-bold uppercase tracking-wider transition-colors duration-300 font-['Monda'] text-xs sm:text-sm relative z-10 ${activeLobby === 'MOBILE' ? 'text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <IconMobile />
                                        <span>MOBILE LOBBY</span>
                                    </div>
                                </button>
                                <button
                                    onClick={() => setActiveLobby('EMULATOR')}
                                    className={`flex-1 py-3 px-4 sm:px-6 rounded-[50px] font-bold uppercase tracking-wider transition-colors duration-300 font-['Monda'] text-xs sm:text-sm relative z-10 ${activeLobby === 'EMULATOR' ? 'text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <IconGamepad />
                                        <span>EMULATOR LOBBY</span>
                                    </div>
                                </button>
                            </div>
                            
                            {/* Right Separator Line */}
                            <div className="flex-1 h-[2px] bg-gradient-to-r from-gray-300 dark:from-white/20 to-transparent"></div>
                        </div>
                    </div>

                    {/* Timeline / Dates Line */}
                    <div className="grid grid-cols-7 gap-2 md:gap-4 mb-8 w-full">
                        {datesList.map((d) => (
                            <button
                                key={d.offset}
                                onClick={() => {
                                    setIsLoading(true);
                                    setTimeout(() => setIsLoading(false), 500);
                                    setSelectedDateOffset(d.offset);
                                }}
                                className={`relative flex flex-col items-center justify-center w-full py-4 px-2 rounded-xl transition-colors duration-300 bg-white dark:bg-[#1a1a24] border ${selectedDateOffset === d.offset ? 'border-transparent z-20' : 'border-gray-100 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/50 z-0'} shadow-sm`}
                            >
                                {selectedDateOffset === d.offset && (
                                    <motion.div
                                        layoutId="activeDateIndicator"
                                        className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl shadow-[0_10px_20px_rgba(236,72,153,0.3)] z-0"
                                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                    />
                                )}
                                <div className={`relative z-10 flex flex-col items-center justify-center transition-colors duration-300 ${selectedDateOffset === d.offset ? 'text-white' : 'text-gray-800 dark:text-gray-200'}`}>
                                    <span className={`text-[9px] sm:text-xs font-bold uppercase tracking-wider mb-1 font-['Monda'] ${selectedDateOffset === d.offset ? 'opacity-90' : 'text-gray-400 dark:text-gray-500'}`}>
                                        {d.offset === 0 ? 'TODAY' : d.offset === 1 ? 'TMW' : d.dayName}
                                    </span>
                                    <span className="text-xl sm:text-3xl font-black mb-1 font-['Monda']">{d.dayNum}</span>
                                    <span className={`text-[9px] sm:text-xs font-bold uppercase font-['Monda'] ${selectedDateOffset === d.offset ? 'opacity-90' : 'text-gray-400 dark:text-gray-500'}`}>{d.month}</span>
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="w-full">
                        <div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 min-[1680px]:grid-cols-4 gap-[5px] lg:gap-[15px]">
                                {matches.length === 0 || isLoading ? (
                                    Array.from({ length: matches.length > 0 && isLoading ? matches.length : 4 }).map((_, i) => {
                                        const [bc1, bc2] = colorsByDate[selectedDateOffset] || ['#a855f7', '#ec4899'];
                                        return (
                                            <div key={`skeleton-${i}`} className="h-full">
                                                <SkeletonCard borderColor1={bc1} borderColor2={bc2} />
                                            </div>
                                        );
                                    })
                                ) : (
                                    <>
                                        {matches.map((card, i) => {
                                            let cardSlotsStr = card.slots || "0 / 0";
                                            if (card.isDummyTesting) {
                                                const total = parseInt(cardSlotsStr.split(' / ')[1]) || 0;
                                                cardSlotsStr = `${total} / ${total}`;
                                            }

                                            const entryValue = parseInt((card.entry || "₹ 0").replace(/\D/g, '')) || 0;
                                            const filledSlots = parseInt(cardSlotsStr.split(' / ')[0]) || 0;
                                            // 16 GLPC * 1.25 = 20 INR equivalent collection per player
                                            const totalCollection = filledSlots * (entryValue * 1.25);
                                            const dynamicPrizePool = Math.floor(totalCollection * 0.85);
                                            const dynamicPrizePoolGLPC = Math.floor(dynamicPrizePool / 1.25);

                                            const [bc1, bc2] = colorsByDate[selectedDateOffset] || ['#a855f7', '#ec4899'];

                                            const parseTime = (timeStr) => {
                                                const [tStr, modifier] = timeStr.split(' ');
                                                let [hours, minutes] = tStr.split(':');
                                                hours = parseInt(hours, 10);
                                                minutes = parseInt(minutes, 10);
                                                if (hours === 12) hours = 0;
                                                if (modifier === 'PM') hours += 12;
                                                return hours * 60 + minutes;
                                            };

                                            let dynamicIsCompleted = card.isCompleted || false;
                                            let dynamicIsOngoing = false;

                                            if (dynamicIsCompleted) {
                                                // Keep it true if admin marked it as concluded
                                            } else if (card.isLiveNow) {
                                                dynamicIsCompleted = false;
                                                dynamicIsOngoing = true;
                                            } else if (selectedDateOffset < 0) {
                                                dynamicIsCompleted = true;
                                            } else if (selectedDateOffset === 0) {
                                                const matchTimeMins = parseTime(card.time);
                                                const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
                                                const currentMins = now.getHours() * 60 + now.getMinutes();
                                                const diff = currentMins - matchTimeMins;

                                                if (diff >= 30) {
                                                    dynamicIsCompleted = true;
                                                } else if (diff >= 0 && diff < 30) {
                                                    dynamicIsOngoing = true;
                                                }
                                            }

                                            const uid1 = gamerUser?.profileData?.gameUid1;
                                            const uid2 = gamerUser?.profileData?.gameUid2;
                                            const hasJoinedServer = card.bookedSlots && card.bookedSlots.some(slot => slot.uid === uid1 || slot.uid === uid2);
                                            const hasJoined = hasJoinedServer;

                                            const updatedCard = {
                                                ...card,
                                                slots: cardSlotsStr,
                                                prize: `${dynamicPrizePoolGLPC} GLPC = ₹ ${dynamicPrizePool}`,
                                                killReward: "DYNAMIC",
                                                borderColor1: bc1,
                                                borderColor2: bc2,
                                                isCompleted: dynamicIsCompleted,
                                                isOngoing: dynamicIsOngoing,
                                                livestreamLink: card.livestreamLink,
                                                docId: window.currentDocId,
                                                dateStr: window.currentDateStr,
                                                initialHasJoined: hasJoined,
                                                lobbyType: card.lobbyType || 'CLEAN LOBBY',
                                                isDummyTesting: card.isDummyTesting,
                                                resultPublished: card.resultPublished || false,
                                                resultsData: card.resultsData
                                            };
                                            if (card.resultPublished && !card.resultsData) {
                                                console.error("DEBUG ERROR: resultPublished is true, but resultsData is MISSING on card:", card.id);
                                            }

                                            return (
                                                <div key={card.id} className="h-full">
                                                    <TournamentCard {...updatedCard} onJoinClick={handleTournamentJoinClick} onIdPassClick={setIdPassModalData} />
                                                </div>
                                            );
                                        })}
                                        {matches.length % 2 !== 0 && (
                                            <div key="dummy-sm-lg" className="hidden sm:block min-[1680px]:hidden h-full">
                                                <SkeletonCard borderColor1={colorsByDate[selectedDateOffset]?.[0] || '#a855f7'} borderColor2={colorsByDate[selectedDateOffset]?.[1] || '#ec4899'} />
                                            </div>
                                        )}
                                        {matches.length % 4 !== 0 && Array.from({ length: 4 - (matches.length % 4) }).map((_, i) => (
                                            <div key={`dummy-desk-${i}`} className="hidden min-[1680px]:block h-full">
                                                <SkeletonCard borderColor1={colorsByDate[selectedDateOffset]?.[0] || '#a855f7'} borderColor2={colorsByDate[selectedDateOffset]?.[1] || '#ec4899'} />
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                <div className="w-full h-px bg-gray-200 dark:bg-white/5 my-16"></div>

                {/* Section: Hall of Fame */}
                <section>
                    <div className="flex flex-col items-center justify-center mb-10">
                        <h2 className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white uppercase tracking-tight text-center flex flex-col md:flex-row items-center justify-center gap-4">
                            <div className="relative inline-flex bg-gray-200 dark:bg-white/10 rounded-full p-1.5 shadow-inner overflow-hidden">
                                <motion.div 
                                    className={`absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] rounded-full z-0 ${activeHall === 'FAME' ? 'bg-gradient-to-r from-yellow-400 to-amber-600 left-1.5 shadow-[0_0_15px_rgba(251,191,36,0.5)]' : 'bg-gradient-to-r from-red-500 to-red-700 right-1.5 shadow-[0_0_15px_rgba(239,68,68,0.5)]'}`}
                                    layoutId="hallToggle"
                                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                                />
                                <button 
                                    onClick={() => setActiveHall('FAME')}
                                    className={`relative z-10 px-6 sm:px-8 py-3 rounded-full font-bold uppercase tracking-wider text-base sm:text-lg transition-colors ${activeHall === 'FAME' ? 'text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
                                >
                                    HALL OF FAME
                                </button>
                                <button 
                                    onClick={() => setActiveHall('SHAME')}
                                    className={`relative z-10 px-6 sm:px-8 py-3 rounded-full font-bold uppercase tracking-wider text-base sm:text-lg transition-colors ${activeHall === 'SHAME' ? 'text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
                                >
                                    HALL OF SHAME
                                </button>
                            </div>
                        </h2>
                        {activeHall === 'FAME' ? (
                            <p className="text-gray-500 dark:text-gray-400 mt-4 font-bold uppercase tracking-widest text-sm text-center">Top 30 Earning Warriors (Last 60 Days)</p>
                        ) : (
                            <p className="text-red-500/80 dark:text-red-400/80 mt-4 font-bold uppercase tracking-widest text-sm text-center">Banned Players & Hackers</p>
                        )}
                    </div>

                    <div className="bg-gray-50 dark:bg-[#1a1a24] rounded-none border border-gray-200 dark:border-white/10 p-[5px] md:p-[15px]">
                        <div className="max-h-[600px] overflow-y-auto overflow-x-auto custom-scrollbar">
                            <table className="w-full table-fixed text-left border-collapse font-['Monda'] min-w-[700px]">
                                <thead className="sticky top-0 bg-gray-50 dark:bg-[#1a1a24] z-10 shadow-sm">
                                    <tr className="border-b border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 text-xs sm:text-sm uppercase tracking-wider">
                                        <th className="w-[10%] py-4 px-2 sm:px-4 font-black whitespace-nowrap border-r border-gray-200 dark:border-white/10 text-center">#</th>
                                        <th className="w-[30%] py-4 px-2 sm:px-4 font-black whitespace-nowrap border-r border-gray-200 dark:border-white/10 text-center">Player</th>
                                        <th className="w-[20%] py-4 px-2 sm:px-4 font-black whitespace-nowrap border-r border-gray-200 dark:border-white/10 text-center">Matches Played</th>
                                        <th className="w-[20%] py-4 px-2 sm:px-4 font-black whitespace-nowrap border-r border-gray-200 dark:border-white/10 text-center">Total XP</th>
                                        <th className="w-[20%] py-4 px-2 sm:px-4 font-black whitespace-nowrap text-center">Total Earnings</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(activeHall === 'FAME' ? isLoadingHallOfFame : isLoadingHallOfShame) ? (
                                        <tr>
                                            <td colSpan="5" className="py-12 px-4 text-center text-gray-500 dark:text-gray-400 text-sm font-['Monda'] font-bold uppercase tracking-wider">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full border-2 border-pink-500 border-t-transparent animate-spin"></div>
                                                    <span>LOADING LEADERBOARD...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (activeHall === 'FAME' ? hallOfFamePlayers.length : hallOfShamePlayers.length) === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="py-12 px-4 text-center text-gray-500 dark:text-gray-400 text-sm font-['Monda'] font-bold uppercase tracking-wider">
                                                {activeHall === 'FAME' ? 'HALL OF FAME DATA WILL BE UPDATED SOON.' : 'NO BANNED PLAYERS RECENTLY. WE KEEP OUR LOBBIES CLEAN.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        (activeHall === 'FAME' ? hallOfFamePlayers : hallOfShamePlayers).map((player) => {
                                            const isFirst = player.rank === 1;
                                            const isSecond = player.rank === 2;
                                            const isThird = player.rank === 3;

                                            let rowClass = "border-b border-gray-100 dark:border-white/5 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors";
                                            let rankStyle = "text-gray-900 dark:text-gray-400";

                                            if (isFirst) {
                                                rowClass = "bg-yellow-50/50 dark:bg-yellow-500/10 border-b border-yellow-200 dark:border-yellow-500/20";
                                                rankStyle = "text-yellow-600 dark:text-yellow-400 text-xl text-shadow-[0_0_10px_rgba(234,179,8,0.5)]";
                                            } else if (isSecond) {
                                                rowClass = "bg-gray-50 dark:bg-gray-300/10 border-b border-gray-200 dark:border-gray-300/20";
                                                rankStyle = "text-gray-500 dark:text-gray-300 text-lg";
                                            } else if (isThird) {
                                                rowClass = "bg-orange-50/50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-500/20";
                                                rankStyle = "text-orange-700 dark:text-orange-400 text-lg";
                                            }

                                            return (
                                                <tr key={player.rank} className={rowClass}>
                                                    <td className={`py-4 px-2 sm:px-4 font-black border-r border-gray-100 dark:border-white/5 text-center ${rankStyle}`}>
                                                        {player.rank}
                                                    </td>
                                                    <td className="py-4 px-2 sm:px-4 whitespace-nowrap border-r border-gray-100 dark:border-white/5 text-center">
                                                        <span className={`font-bold text-sm sm:text-base uppercase ${isFirst ? 'text-yellow-700 dark:text-yellow-400' : 'text-gray-900 dark:text-white'}`}>{player.name}</span>
                                                    </td>
                                                    <td className="py-4 px-2 sm:px-4 text-gray-800 dark:text-gray-200 text-sm font-bold whitespace-nowrap text-center border-r border-gray-100 dark:border-white/5">
                                                        {player.matchesPlayed}
                                                    </td>
                                                    <td className="py-4 px-2 sm:px-4 text-purple-600 dark:text-purple-400 text-sm font-bold whitespace-nowrap border-r border-gray-100 dark:border-white/5 text-center">
                                                        {player.xp.toLocaleString('en-IN')} XP
                                                    </td>
                                                    <td className="py-4 px-2 sm:px-4 whitespace-nowrap text-center">
                                                        <span className={`font-black text-sm sm:text-lg ${isFirst ? 'text-yellow-600 dark:text-yellow-400' : 'text-pink-600 dark:text-pink-400'}`}>
                                                            ₹ {player.earnings.toLocaleString('en-IN')}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        }))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                <div className="w-full h-px bg-gray-200 dark:bg-white/5 my-16"></div>

                {/* Section 8: Community Feedback */}
                <section>
                    <h2 className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white mb-6 text-center uppercase tracking-tight">COMMUNITY <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-500">FEEDBACK</span></h2>
                    <p className="text-center text-gray-500 dark:text-gray-400 max-w-2xl mx-auto mb-12 font-['Monda'] uppercase text-xs md:text-sm tracking-wider">
                        See what our top players have to say about their experience on the most trusted BGMI tournament platform.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { name: "Sc0utOP", username: "@scout_pubg", earnings: "WON ₹45,000", quote: "The anti-cheat here is insane. Finally a platform where I can play without worrying about hackers. Withdrawals are super fast!", avatar: "https://i.pravatar.cc/150?u=scout" },
                            { name: "Mortal", username: "@ig_mortal", earnings: "WON ₹32,000", quote: "Best BGMI eSports platform right now. The entry fees are reasonable and the prize pools are always guaranteed. Loving it!", avatar: "https://i.pravatar.cc/150?u=mortal" },
                            { name: "Jonathan", username: "@jonathan_gaming", earnings: "WON ₹58,500", quote: "I've played on many platforms, but this one is the smoothest. The UI is great and getting the room ID is always on time.", avatar: "https://i.pravatar.cc/150?u=jonathan" },
                        ].map((review, i) => (
                            <div key={i} className="bg-white dark:bg-[#1a1a24] rounded-2xl p-6 border border-gray-100 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/50 transition-colors shadow-xl relative group overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-5 dark:opacity-10 transform translate-x-2 -translate-y-2 group-hover:scale-110 group-hover:-rotate-12 transition-transform duration-500">
                                    <svg width="80" height="80" viewBox="0 0 24 24" fill="currentColor" className="text-purple-600 dark:text-purple-400"><path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/></svg>
                                </div>
                                <div className="flex items-center gap-4 mb-5 relative z-10">
                                    <img src={review.avatar} alt={review.name} className="w-14 h-14 rounded-full border-2 border-purple-500 p-0.5 object-cover" />
                                    <div>
                                        <h4 className="font-bold text-gray-900 dark:text-white uppercase font-['Iceland'] text-xl tracking-wider">{review.name}</h4>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 font-['Monda'] uppercase tracking-widest">{review.username}</p>
                                    </div>
                                </div>
                                <div className="mb-4 relative z-10">
                                    <span className="inline-block bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase font-['Monda'] border border-green-200 dark:border-green-500/30">
                                        {review.earnings}
                                    </span>
                                </div>
                                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed relative z-10 font-['Monda'] italic">
                                    "{review.quote}"
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                <div className="w-full h-px bg-gray-200 dark:bg-white/5 my-16"></div>




            </div>


            {/* Section 11: Premium Footer */}
            <BgmiFooter />
        </div>
    );
}
